import { useEffect } from 'react';
import { toast } from '@/hooks/use-toast';

interface ErrorHandlerOptions {
  showToast?: boolean;
  logToConsole?: boolean;
  logToService?: boolean;
}

export function useErrorHandler(options: ErrorHandlerOptions = {}) {
  const {
    showToast = true,
    logToConsole = true,
    logToService = false
  } = options;

  useEffect(() => {
    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      if (logToConsole) {
        console.group('🚨 Unhandled Promise Rejection');
        console.error('Reason:', event.reason);
        console.error('Promise:', event.promise);
        console.groupEnd();
      }

      if (showToast) {
        toast({
          title: "Network Error",
          description: "A network request failed. Please check your connection and try again.",
          variant: "destructive",
        });
      }

      if (logToService) {
        // Log to your error monitoring service
        logErrorToService('UnhandledPromiseRejection', event.reason);
      }

      // Prevent the default browser error handling
      event.preventDefault();
    };

    // Handle global JavaScript errors
    const handleGlobalError = (event: ErrorEvent) => {
      console.error('Global error:', event.error);
      
      if (logToConsole) {
        console.group('🚨 Global Error');
        console.error('Error:', event.error);
        console.error('Message:', event.message);
        console.error('Filename:', event.filename);
        console.error('Line:', event.lineno);
        console.error('Column:', event.colno);
        console.groupEnd();
      }

      if (showToast) {
        toast({
          title: "Application Error",
          description: "An unexpected error occurred. The page may need to be refreshed.",
          variant: "destructive",
        });
      }

      if (logToService) {
        logErrorToService('GlobalError', event.error);
      }
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleGlobalError);

    // Cleanup
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleGlobalError);
    };
  }, [showToast, logToConsole, logToService]);

  // Return a manual error handler function
  const handleError = (error: Error | unknown, context?: string) => {
    console.error(`Error in ${context || 'unknown context'}:`, error);
    
    if (logToConsole) {
      console.group(`🚨 Manual Error Handler - ${context || 'Unknown Context'}`);
      console.error('Error:', error);
      console.error('Context:', context);
      console.groupEnd();
    }

    if (showToast) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }

    if (logToService) {
      logErrorToService(context || 'ManualError', error);
    }
  };

  return { handleError };
}

// Helper function to log errors to external service
function logErrorToService(type: string, error: any) {
  try {
    const errorData = {
      type,
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: localStorage.getItem('userId') || 'anonymous', // if you store user ID
    };

    // In production, send to your monitoring service
    if (process.env.NODE_ENV === 'production') {
      // Example: send to your API endpoint
      // fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorData)
      // }).catch(console.error);
    }

    // For development, just log to console
    if (process.env.NODE_ENV === 'development') {
      console.log('Would log to service:', errorData);
    }
  } catch (loggingError) {
    console.error('Failed to log error to service:', loggingError);
  }
} 